import { useEffect, useRef } from 'react'
import { delayBufferManager } from './delay-buffer-manager.ts'
import { dspscript } from './dsp-script.ts'
import { animationManager } from './animation-manager.ts'

interface ScrollingVisualizerProps {
  isPlaying: boolean
  className?: string
  scrollStep?: number
}

export function ScrollingVisualizer({ isPlaying, className, scrollStep }: ScrollingVisualizerProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const initializedRef = useRef(false)
  // Use separate delay buffer from Visualizers to avoid conflicts
  const delayBufferIdRef = useRef('scrolling-visualizer')
  const delayBufferReadPtrRef = useRef(0)
  const delayBufferFillLevelRef = useRef(0)
  const scrollRateRef = useRef(100)
  const lastScrollTimeRef = useRef<number | null>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return

    const resize = () => {
      const dpr = window.devicePixelRatio || 1
      const parent = canvas.parentElement
      if (!parent) return

      const parentRect = parent.getBoundingClientRect()
      const cssWidth = parentRect.width
      const cssHeight = parentRect.height

      if (cssWidth > 0 && cssHeight > 0) {
        const width = Math.max(1, Math.floor(cssWidth * dpr))
        const height = Math.max(1, Math.floor(cssHeight * dpr))
        canvas.width = width
        canvas.height = height
        // Set CSS size to 100% to maintain responsive sizing while preventing canvas from using internal resolution
        canvas.style.width = '100%'
        canvas.style.height = '100%'
      }
    }

    const scheduleResize = () => {
      requestAnimationFrame(() => {
        requestAnimationFrame(resize)
      })
    }

    scheduleResize()
    window.addEventListener('resize', scheduleResize)

    const parent = canvas.parentElement
    const ro = parent ? new ResizeObserver(scheduleResize) : null
    if (ro && parent) {
      ro.observe(parent)
    }

    return () => {
      window.removeEventListener('resize', scheduleResize)
      if (ro && parent) {
        ro.unobserve(parent)
      }
      initializedRef.current = false
    }
  }, [])

  useEffect(() => {
    const delayBufferId = delayBufferIdRef.current
    delayBufferManager.getOrCreateDelayBuffer(delayBufferId)

    return () => {
      delayBufferManager.removeDelayBuffer(delayBufferId)
    }
  }, [])

  useEffect(() => {
    if (!isPlaying) {
      const delayBuffer = delayBufferManager.getOrCreateDelayBuffer(delayBufferIdRef.current)
      delayBuffer.fillLevel = 0
      delayBuffer.writePtr = 0
      delayBufferReadPtrRef.current = 0
      delayBufferFillLevelRef.current = 0
      lastScrollTimeRef.current = null
      initializedRef.current = false
      return
    }

    const canvas = canvasRef.current
    if (!canvas) return

    const audioContext = dspscript.getAudioContext()
    const sampleRate = audioContext?.sampleRate ?? 48000

    // Calculate scroll rate: pixels per second (update when scrollStep changes)
    const pixelsPerSecond = (scrollStep ?? 1) * 60
    scrollRateRef.current = pixelsPerSecond

    if (!initializedRef.current) {
      const delayBuffer = delayBufferManager.getOrCreateDelayBuffer(delayBufferIdRef.current)
      delayBufferReadPtrRef.current = delayBuffer.readPtr
      delayBufferFillLevelRef.current = delayBuffer.fillLevel
      lastScrollTimeRef.current = null
      initializedRef.current = true
    }

    const animate = () => {
      const canvas = canvasRef.current
      if (!canvas) return

      const ctx2d = canvas.getContext('2d', { willReadFrequently: true })
      if (!ctx2d) return
      ctx2d.imageSmoothingEnabled = false

      if (!Number.isFinite(canvas.width) || !Number.isFinite(canvas.height) || canvas.width <= 0
        || canvas.height <= 0)
      {
        return
      }

      // Update delay buffer manager (reads from ring analyser once for all components)
      delayBufferManager.update()

      const delayBuffer = delayBufferManager.getOrCreateDelayBuffer(delayBufferIdRef.current)
      const delayBufferSize = delayBuffer.left.length
      const delaySamples = Math.floor(sampleRate * 0.1)
      const minFillLevel = delaySamples

      // Initialize read pointer from delay buffer
      if (delayBufferReadPtrRef.current !== delayBuffer.readPtr) {
        delayBufferReadPtrRef.current = delayBuffer.readPtr
      }

      // Calculate available samples (distance from read to write, wrapping around)
      const writePtr = delayBuffer.writePtr
      const distance = (writePtr - delayBufferReadPtrRef.current + delayBufferSize) % delayBufferSize
      const availableSamples = Math.min(distance, delayBufferSize)

      // Wait for delay buffer to fill before starting to scroll
      if (availableSamples < minFillLevel) {
        if (lastScrollTimeRef.current !== null) {
          lastScrollTimeRef.current = null
        }
        return
      }

      const now = performance.now()
      const pixelsPerSecond = scrollRateRef.current
      const pixelsPerMs = pixelsPerSecond / 1000

      if (lastScrollTimeRef.current === null) {
        lastScrollTimeRef.current = now
        return
      }

      const elapsedMs = now - lastScrollTimeRef.current
      const pixelsToScroll = Math.floor(elapsedMs * pixelsPerMs)

      if (pixelsToScroll <= 0) {
        return
      }

      // Calculate how many samples correspond to this time period
      const elapsedSeconds = elapsedMs / 1000
      const samplesForTimePeriod = Math.floor(sampleRate * elapsedSeconds)

      // Only read what's available after syncing
      if (availableSamples < samplesForTimePeriod) {
        return
      }

      const samplesToRead = Math.min(samplesForTimePeriod, availableSamples)

      // Scroll canvas
      if (canvas.width > pixelsToScroll) {
        const imageData = ctx2d.getImageData(pixelsToScroll, 0, canvas.width - pixelsToScroll, canvas.height)
        ctx2d.putImageData(imageData, 0, 0)
        ctx2d.clearRect(canvas.width - pixelsToScroll, 0, pixelsToScroll, canvas.height)
      }
      else {
        ctx2d.clearRect(0, 0, canvas.width, canvas.height)
      }

      // Draw new pixels at the right edge, distributing samples across pixels
      const samplesPerPixel = samplesToRead / pixelsToScroll
      for (let p = 0; p < pixelsToScroll; p++) {
        const x = canvas.width - pixelsToScroll + p
        if (x < 0 || x >= canvas.width) continue

        const sampleStart = Math.floor(p * samplesPerPixel)
        const sampleEnd = p === pixelsToScroll - 1
          ? samplesToRead
          : Math.floor((p + 1) * samplesPerPixel)
        let peak = 0

        for (let s = sampleStart; s < sampleEnd && s < samplesToRead; s++) {
          const readIdx = (delayBufferReadPtrRef.current + s) % delayBufferSize
          const sample = (delayBuffer.left[readIdx]! + delayBuffer.right[readIdx]!) * 0.5
          peak = Math.max(peak, Math.abs(sample))
        }

        if (Number.isFinite(peak)) {
          const ampBarHeight = Math.max(1, peak * canvas.height)
          const ampY = (canvas.height - ampBarHeight) / 2

          if (Number.isFinite(ampBarHeight) && Number.isFinite(ampY)) {
            const grad = ctx2d.createLinearGradient(x, ampY, x, ampY + ampBarHeight)
            if (peak > 1) {
              grad.addColorStop(0, '#f00')
              grad.addColorStop(0.5, '#f00')
              grad.addColorStop(1, '#f00')
            }
            else {
              grad.addColorStop(0.2, '#999')
              grad.addColorStop(0.5, '#ddd')
              grad.addColorStop(0.8, '#999')
            }
            ctx2d.fillStyle = grad
            ctx2d.fillRect(x, ampY, 1, ampBarHeight)
          }
        }
      }

      // Update read pointer and fill level
      delayBufferReadPtrRef.current = (delayBufferReadPtrRef.current + samplesToRead) % delayBufferSize
      delayBuffer.readPtr = delayBufferReadPtrRef.current
      delayBuffer.fillLevel -= samplesToRead
      delayBufferFillLevelRef.current = delayBuffer.fillLevel
      lastScrollTimeRef.current = now
    }

    const unregister = animationManager.register(animate)

    return () => {
      unregister()
    }
  }, [isPlaying, scrollStep])

  return (
    <canvas
      ref={canvasRef}
      className={className ?? 'absolute inset-0 w-full h-full pointer-events-none opacity-40'}
    />
  )
}
